import { useTranslation } from "react-i18next";
// src/pages/Settings.tsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { hasUpgradeIntent } from "../utils/upgradeIntent";
import {
  BanknotesIcon,
  CreditCardIcon,
  ArrowLeftIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  TagIcon,
  ShieldExclamationIcon,
  EnvelopeIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { companyRoute } from "../utils/routeHelpers";
import { apiGet } from "../api/client";
import { AuthMe } from "../types/auth";
import UpgradeModal from "../components/UpgradeModal";
import { openPortal, confirmPaidCheckout } from "../api/billing";
import ProBadge from "../components/ProBadge";
import { A } from "../styles/appleClasses";

export default function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();

  const [me, setMe] = useState<AuthMe | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Bounded post-Stripe intro-trial activation refresh (see effect below).
  // `activationRun` is intentionally decoupled from location.search: arming
  // it (incrementing) kicks off the retry effect, and it is never touched
  // by the billing-return effect's own URL cleanup, so stripping
  // billing/session_id/intro_trial from the URL cannot cancel retries that
  // are already in flight.
  const [activationBanner, setActivationBanner] = useState<
    "none" | "pending" | "fallback"
  >("none");
  const [activationRun, setActivationRun] = useState(0);

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("access_token");
    setMe(null);
    setShowUpgrade(false);
    apiGet<AuthMe>("/auth/me")
      .then(user => {
        if (active && localStorage.getItem("access_token") === token) setMe(user);
      })
      .catch(() => { if (active) setMe(null); });
    return () => { active = false; };
  }, [company_id]);

  const [paidConfirmation, setPaidConfirmation] = useState(false);
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (!sp.has("membership_checkout")) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    const waitForNextCheck = () => new Promise<void>(resolve => { timer = setTimeout(resolve, 2000); });
    (async () => {
      try {
        if (sp.get("membership_checkout") === "1") {
          setPaidConfirmation(true);
          for (let check = 0; check < 4 && alive && !controller.signal.aborted; check++) {
            await confirmPaidCheckout(undefined, controller.signal);
            if (!alive || controller.signal.aborted) return;
            const updated = await apiGet<AuthMe>("/auth/me");
            if (!alive || controller.signal.aborted) return;
            setMe(updated);
            if (updated.is_user_pro) { setPaidConfirmation(false); break; }
            if (check < 3) await waitForNextCheck();
          }
        }
      } catch { /* Confirmation remains pending; query flags never grant access. */ }
      finally {
        window.clearTimeout(timeout);
        if (alive) {
          sp.delete("membership_checkout");
          navigate({ pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" }, { replace: true });
        }
      }
    })();
    return () => { alive = false; controller.abort(); window.clearTimeout(timeout); if (timer) clearTimeout(timer); };
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const billing = sp.get("billing");
    const sessionId = sp.get("session_id");
    const introTrial = sp.get("intro_trial");
    if (!billing) return;

    // The bounded activation-refresh sequence (effect below) is armed only
    // for a successful intro-trial return: billing=1 AND intro_trial=1 AND
    // a session_id present. A plain (non-intro-trial) billing=1 return keeps
    // today's single-shot /auth/me refresh and nothing more; billing=0
    // (cancel) and ordinary visits with no billing param at all never call
    // /auth/me from this effect.
    const isIntroTrialSuccessReturn =
      billing === "1" && introTrial === "1" && !!sessionId;

    let alive = true;

    (async () => {
      try {
        if (billing === "1" && sessionId) {
          // This is also activation check #1 of at most 4 total for an
          // intro-trial return — the bounded retry effect below performs
          // checks #2-#4, it never repeats this one.
          const updated = await apiGet<AuthMe>("/auth/me").catch(() => null);
          if (!alive) return;
          if (updated) setMe(updated);

          if (isIntroTrialSuccessReturn) {
            if (updated?.is_user_pro) {
              setActivationBanner("none");
            } else {
              setActivationBanner("pending");
              // Arm the bounded retry effect. A plain counter increment
              // (rather than a boolean) guarantees a fresh dependency
              // change even if a prior run also left activationBanner
              // "pending", so React never bails out of re-running it.
              setActivationRun((n) => n + 1);
            }
          }
        }
      } finally {
        sp.delete("billing");
        sp.delete("session_id");
        sp.delete("intro_trial");
        navigate(
          {
            pathname: location.pathname,
            search: sp.toString() ? `?${sp.toString()}` : "",
          },
          { replace: true },
        );
      }
    })();

    return () => {
      alive = false;
    };
  }, [location.search, location.pathname, navigate]);

  // Bounded activation-refresh retries (checks #2-#4). Deliberately its own
  // effect, keyed only on `activationRun` rather than on location.search:
  // the billing-return effect above strips billing/session_id/intro_trial
  // from the URL immediately (as it always has), which changes
  // location.search and re-runs that effect — if these timers lived there
  // instead, that same cleanup would cancel the retries it had just
  // scheduled. Keying on `activationRun` means URL cleanup can happen
  // immediately without affecting the retry sequence's lifetime.
  useEffect(() => {
    if (activationRun === 0) return;

    let alive = true;
    let timerId: ReturnType<typeof window.setTimeout> | null = null;

    const runRetry = (retryNumber: number) => {
      timerId = window.setTimeout(async () => {
        timerId = null;
        if (!alive) return;
        const updated = await apiGet<AuthMe>("/auth/me").catch(() => null);
        if (!alive) return;
        if (updated) setMe(updated);

        if (updated?.is_user_pro) {
          setActivationBanner("none");
          return;
        }
        if (retryNumber >= 3) {
          setActivationBanner("fallback");
          return;
        }
        runRetry(retryNumber + 1);
      }, 2000);
    };

    runRetry(1);

    return () => {
      alive = false;
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, [activationRun]);

  const isPro = !!me?.is_user_pro;
  const isCompanyAdmin = !!me?.is_company_admin;

  useEffect(() => {
    if (!me || !hasUpgradeIntent(location.search)) return;
    const search = new URLSearchParams(location.search);
    search.delete("upgrade");
    const allowed = me.is_active && me.setup_complete && String(me.company_id) === company_id
      && (me.is_company_admin || me.is_ricebook_admin);
    if (allowed && !me.is_user_pro) setShowUpgrade(true);
    // Consume navigation intent, never payment proof. No sticky browser storage.
    navigate({ pathname: location.pathname, search: search.toString() ? `?${search}` : "" }, { replace: true });
  }, [me, company_id, location.pathname, location.search, navigate]);

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const headerShell =
    "sticky top-0 z-30 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 border-b border-gray-100";

  const headerInner =
    "w-full max-w-3xl mx-auto px-2 sm:px-6 pt-4 pb-2 mb-2 flex items-center";

  const tileBase =
    `relative flex flex-col items-center justify-center ` +
    `h-[180px] sm:h-[220px] ` +
    `${A.card} ` +
    `px-4 py-6 sm:px-6 sm:py-8 ` +
    `hover:shadow-md hover:border-gray-300 transition ` +
    `focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  const lockedTile =
    `${tileBase} opacity-70 cursor-pointer select-none`;

  const tileTitle = `${A.title} text-center leading-tight`;
  const tileDesc = `${A.label} mt-1 text-center`;
  const tileIcon = "w-7 h-7 mb-2 text-appleblue";

  const titleWithBreak = (title: string) => {
    const split = title.lastIndexOf(" ");
    return split < 0 ? title : (
      <>{title.slice(0, split)} <br className="hidden sm:block" /> {title.slice(split + 1)}</>
    );
  };

  const openUpgrade = () => setShowUpgrade(true);

  const handleLockedKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openUpgrade();
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {/* Header */}
      <div className={headerShell}>
        <div className={headerInner}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <h1 className={`flex-1 text-center ${A.title}`}>{t("navigation.settings")}</h1>

          <button
            type="button"
            onClick={() => navigate(companyRoute(company_id, "/"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      <div className="w-full max-w-3xl mx-auto px-2 sm:px-6 pb-8">
        {paidConfirmation && <p role="status" className="mb-4 text-sm">
          {t("sales.upgrade.paidPending", { defaultValue: "Payment confirmation is pending. Check its status before starting another checkout." })}
        </p>}
        {activationBanner !== "none" && (
          <div
            role="status"
            aria-live="polite"
            data-testid="intro-trial-activation-banner"
            className={[
              "mb-4 rounded-2xl border px-4 py-3",
              A.body,
              activationBanner === "pending"
                ? "border-sky-100 bg-sky-50 text-sky-800"
                : "border-amber-100 bg-amber-50 text-amber-800",
            ].join(" ")}
          >
            {activationBanner === "pending"
              ? t("settings.activationPending")
              : t("settings.activationFallback")}
          </div>
        )}

        {/* Core Settings */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Link
            to={companyRoute(company_id, "/settings/accounts")}
            className={tileBase}
          >
            <BanknotesIcon className={tileIcon} />
            <span className={tileTitle}>
              {titleWithBreak(t("settings.bankCashAccounts"))}
            </span>
            <span className={tileDesc}>
              {t("settings.bankCashDescription")}
            </span>
          </Link>

          <Link
            to={companyRoute(company_id, "/settings/payment-methods")}
            className={tileBase}
          >
            <CreditCardIcon className={tileIcon} />
            <span className={tileTitle}>
              {titleWithBreak(t("settings.paymentMethods"))}
            </span>
            <span className={tileDesc}>{t("settings.paymentMethodsDescription")}</span>
          </Link>

          <Link
            to={companyRoute(company_id, "/settings/company-profile")}
            className={tileBase}
          >
            <BuildingOffice2Icon className={tileIcon} />
            <span className={tileTitle}>
              {titleWithBreak(t("settings.companyProfile"))}
            </span>
            <span className={tileDesc}>{t("settings.companyProfileDescription")}</span>
          </Link>

          <Link
            to={companyRoute(company_id, "/settings/editexpensecategories")}
            className={tileBase}
          >
            <TagIcon className={tileIcon} />
            <span className={tileTitle}>
              {t("expenses.categories.title")}
            </span>
            <span className={tileDesc}>{t("expenses.categories.categoryDescription")}</span>
          </Link>

          <Link
            to={companyRoute(company_id, "/settings/privacy-controls")}
            className={tileBase}
          >
            <ShieldExclamationIcon className={tileIcon} />
            <span className={tileTitle}>{t("settings.privacyManagement")}</span>
            <span className={tileDesc}>{t("settings.privacyDescription")}</span>
          </Link>
        </div>

        {/* Pro Features */}
        <div className="mt-8 mb-3 flex items-center gap-2">
          <ProBadge size="xs" />
          <span className={A.title}>{t("settings.proFeatures")}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Invoice Sending */}
          {isPro ? (
            <Link
              to={companyRoute(company_id, "/settings/invoice-email")}
              className={tileBase}
            >
              <EnvelopeIcon className={tileIcon} />
              <span className={tileTitle}>{t("settings.invoiceSending")}</span>
              <span className={tileDesc}>{t("settings.invoiceSendingDescription")}</span>
            </Link>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={openUpgrade}
              onKeyDown={handleLockedKeyDown}
              className={lockedTile}
            >
              <ProBadge overlay size="xs" />
              <EnvelopeIcon className={tileIcon} />
              <span className={tileTitle}>{t("settings.invoiceSending")}</span>
              <span className={tileDesc}>{t("settings.invoiceSendingLockedDescription")}</span>
              <span className="text-[11px] text-gray-500 mt-2 text-center font-medium">
                {t("settings.upgradeToUnlock")}
              </span>
            </div>
          )}

          {/* Staff Invite */}
          {isCompanyAdmin &&
            (isPro ? (
              <Link
                to={companyRoute(company_id, "/settings/staff-invite")}
                className={tileBase}
              >
                <UserPlusIcon className={tileIcon} />
                <span className={tileTitle}>{t("settings.staffInvite")}</span>
                <span className={tileDesc}>{t("settings.staffInviteDescription")}</span>
              </Link>
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={openUpgrade}
                onKeyDown={handleLockedKeyDown}
                className={lockedTile}
              >
                <ProBadge overlay size="xs" />
                <UserPlusIcon className={tileIcon} />
                <span className={tileTitle}>{t("settings.staffInvite")}</span>
                <span className={tileDesc}>{t("settings.staffInviteDescription")}</span>
                <span className="text-[11px] text-gray-500 mt-2 text-center font-medium">
                  {t("settings.upgradeToUnlock")}
                </span>
              </div>
            ))}
        </div>

        {/* Subscription */}
        <div className="mt-8 mb-3 flex items-center gap-2">
          <span className={A.title}>{t("settings.subscription")}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {!isPro ? (
            <button type="button" onClick={openUpgrade} className={tileBase}>
              <div className="mb-2">
                <ProBadge size="sm" />
              </div>
              <span className={tileTitle}>{t("sales.upgrade.title")}</span>
              <span className={tileDesc}>
                {t("settings.upgradeDescription")}
              </span>
            </button>
          ) : (
            <button
              type="button"
              onClick={async () => {
                try {
                  const { url } = await openPortal();
                  window.location.assign(url);
                } catch (err) {
                  console.error("Failed to open billing portal", err);
                  alert(t("settings.portalFailed"));
                }
              }}
              className={tileBase}
              aria-label={t("settings.managePro")}
            >
              <div className="mb-2">
                <ProBadge size="sm" />
              </div>
              <span className={tileTitle}>{t("settings.managePro")}</span>
              <span className={tileDesc}>{t("settings.manageProDescription")}</span>
            </button>
          )}
        </div>
      </div>

      {showUpgrade && (
        <UpgradeModal
          open={showUpgrade}
          onClose={() => setShowUpgrade(false)}
        />
      )}
    </div>
  );
}
